const EARTH_RADIUS_KM = 6371;
const MAX_RADIUS_KM = 10;
const MIN_RESULTS = 10;
const MAX_RESULTS = 15;

function toRad(deg) {
  return deg * (Math.PI / 180);
}

function haversineDistance(lat1, lon1, lat2, lon2) {
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function parseCoords(str) {
  const parts = String(str).split(',').map((s) => parseFloat(s.trim()));
  return { lat: parts[0], lon: parts[1] };
}

function isValidCoords({ lat, lon }) {
  return !isNaN(lat) && !isNaN(lon);
}

/**
 * Minimum distance from a point to a route, considering start, stop and every
 * GPX trackpoint. Exits early once within MAX_RADIUS_KM.
 */
function minRouteDistance(lat, lon, route) {
  const start = parseCoords(route.start);
  let min = isValidCoords(start)
    ? haversineDistance(lat, lon, start.lat, start.lon)
    : Infinity;

  if (min <= MAX_RADIUS_KM) return min;

  const stop = parseCoords(route.stop);
  if (isValidCoords(stop)) {
    min = Math.min(min, haversineDistance(lat, lon, stop.lat, stop.lon));
    if (min <= MAX_RADIUS_KM) return min;
  }

  const track = route.gpx?.track;
  if (Array.isArray(track)) {
    for (const point of track) {
      const d = haversineDistance(lat, lon, point[0], point[1]);
      if (d < min) min = d;
      if (min <= MAX_RADIUS_KM) return min;
    }
  }

  return min;
}

/**
 * Greedy sparse sampling: picks `count` routes that are maximally spread out
 * from each other, starting from the route closest to the selected location.
 */
function sparseSample(candidates, count) {
  if (candidates.length <= count) return [...candidates];

  const selected = [candidates[0]];
  const remaining = candidates.slice(1);

  while (selected.length < count && remaining.length > 0) {
    let bestIdx = 0;
    let bestMinDist = -Infinity;

    for (let i = 0; i < remaining.length; i++) {
      const minDist = Math.min(
        ...selected.map((s) =>
          haversineDistance(s._lat, s._lon, remaining[i]._lat, remaining[i]._lon)
        )
      );
      if (minDist > bestMinDist) {
        bestMinDist = minDist;
        bestIdx = i;
      }
    }

    selected.push(remaining[bestIdx]);
    remaining.splice(bestIdx, 1);
  }

  return selected;
}

/**
 * Greedy density-based clustering. Repeatedly picks the start point with the
 * most unclaimed neighbours within `radiusKm`, records it as a cluster
 * centroid, then removes those neighbours from the pool. Singletons
 * (clusters with fewer than `minCount` routes) are dropped.
 *
 * @param {Array}  routes
 * @param {number} radiusKm  - Zone radius (default 10)
 * @param {number} minCount  - Minimum routes for a cluster to be kept (default 2)
 * @returns {Array<{ lat, lon, count }>} sorted by descending count
 */
export function computeLocationSuggestions(routes, radiusKm = MAX_RADIUS_KM, minCount = 2) {
  const points = routes
    .map((r) => parseCoords(r.start))
    .filter(isValidCoords);

  const remaining = [...points];
  const clusters = [];

  while (remaining.length > 0) {
    // Find the point whose 10 km neighbourhood is densest
    let bestMembers = [];

    for (const candidate of remaining) {
      const members = remaining.filter(
        (p) => haversineDistance(candidate.lat, candidate.lon, p.lat, p.lon) <= radiusKm
      );
      if (members.length > bestMembers.length) {
        bestMembers = members;
      }
    }

    // Compute centroid of the winning neighbourhood
    const centLat = bestMembers.reduce((s, p) => s + p.lat, 0) / bestMembers.length;
    const centLon = bestMembers.reduce((s, p) => s + p.lon, 0) / bestMembers.length;

    if (bestMembers.length >= minCount) {
      clusters.push({ lat: centLat, lon: centLon, count: bestMembers.length });
    }

    // Remove claimed points
    const claimed = new Set(bestMembers.map((p) => `${p.lat}|${p.lon}`));
    for (let i = remaining.length - 1; i >= 0; i--) {
      if (claimed.has(`${remaining[i].lat}|${remaining[i].lon}`)) {
        remaining.splice(i, 1);
      }
    }
  }

  return clusters.sort((a, b) => b.count - a.count);
}

/**
 * Filter routes by proximity to a location. A route is included when its
 * start, stop, OR any GPX trackpoint lies within 10 km of the selected centre.
 * Returns between MIN_RESULTS and MAX_RESULTS routes.
 *
 * @param {Array}  routes
 * @param {{ lat: number, lon: number }} location
 * @returns {Array}
 */
export function filterRoutes(routes, location) {
  const { lat, lon } = location;

  const candidates = routes
    .map((route) => {
      const startCoords = parseCoords(route.start);
      const distanceKm = minRouteDistance(lat, lon, route);
      return {
        ...route,
        _lat: isValidCoords(startCoords) ? startCoords.lat : lat,
        _lon: isValidCoords(startCoords) ? startCoords.lon : lon,
        _distanceKm: distanceKm,
      };
    })
    .filter((r) => r._distanceKm <= MAX_RADIUS_KM)
    .sort((a, b) => a._distanceKm - b._distanceKm);

  if (candidates.length <= MIN_RESULTS) return candidates;

  return sparseSample(candidates, MAX_RESULTS).sort(() => Math.random() - 0.5);
}
